import { alpha, Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import { colorsPalette, MIDDLE_MARGIN, SMALL_MARGIN, TINY_MARGIN } from '../../theme';

const useStyles = (pageYOffset, isHeaderHidden) =>
  makeStyles((theme: Theme) => {
    return createStyles({
      header: {
        ...(isHeaderHidden && { display: 'none' }),
        transition: 'background .5s ease-in-out',
        background: pageYOffset > 0 ? colorsPalette.blueDark : 'transparent',
        boxShadow: pageYOffset > 0 ? `0px 3px 6px ${alpha(colorsPalette.black, 0.4)}` : 'none',

        [theme.breakpoints.up('md')]: {
          background: pageYOffset > 0 ? alpha(colorsPalette.blueDark, 0.85) : 'transparent',
          boxShadow: 'none',
        },
      },
      toolBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        fontSize: '18px',
        transition: 'all .5s ease-in-out',
        [theme.breakpoints.up('sm')]: {
          padding: pageYOffset > 0 ? '10px 16px' : '30px 16px',
          fontSize: '16px',
        },
        [theme.breakpoints.up('md')]: {
          padding: pageYOffset > 0 ? '10px 24px' : '30px 24px',
        },
        [theme.breakpoints.up('xl')]: {
          padding: pageYOffset > 0 ? '10px 30px' : '30px',
        },
        [theme.breakpoints.up('ml')]: {
          width: '100%',
          maxWidth: theme.breakpoints.values.ml,
          margin: '0 auto',
        },
      },
      topBar: {
        display: 'flex',
        flexDirection: 'row-reverse',
        justifyContent: 'flex-start',
        alignItems: 'center',
        zIndex: 1300,
        height: 54,
        marginLeft: SMALL_MARGIN,

        [theme.breakpoints.up('md')]: {
          marginLeft: MIDDLE_MARGIN,
        },

        '&$openSearch': {
          flexGrow: 1,
          paddingLeft: 40,
          [theme.breakpoints.up('sm')]: {
            justifyContent: 'flex-end',
          },
          [theme.breakpoints.up('md')]: {
            marginRight: 0,
            justifyContent: 'flex-start',
          },
        },
        '& > $username': {
          display: 'none',

          '@media only screen and (min-width: 1600px)': {
            display: 'block',
          },
        },
      },
      topNavMenu: {
        display: 'none',
        [theme.breakpoints.up('xl')]: {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
      topNavButton: {
        height: 24,
        padding: 0,
        marginRight: 24,
        [theme.breakpoints.up('md')]: {
          display: 'none',
        },
        '&$openSearch': {
          [theme.breakpoints.down('lg')]: {
            display: 'none',
          },
        },
      },
      topNavIcon: {
        width: 20,
        height: 20,
        [theme.breakpoints.up('xl')]: {
          display: 'none',
        },
      },
      leftPart: {
        display: 'flex',
      },
      logoWrapper: {
        display: 'flex',
        height: 45,
        alignItems: 'center',
        transition: 'all .5s ease-in-out',
        zIndex: 1300,
        marginRight: SMALL_MARGIN,
        '& img': {
          height: 32,
        },
        '@media only screen and (min-width: 375px)': {
          '& img': {
            height: 40,
          },
        },
        '@media only screen and (min-width: 425px)': {
          '& img': {
            height: 45,
          },
        },
        [theme.breakpoints.up('md')]: {
          height: 60,
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: '0%',
          '& img': {
            height: pageYOffset > 0 ? 45 : 60,
            transition: 'all .5s ease-in-out',
          },
          marginRight: MIDDLE_MARGIN,
        },
        '&$openSearch': {
          display: 'none',

          [theme.breakpoints.up('sm')]: {
            display: 'block',
            flexGrow: 0,
          },
        },
      },
      joinBtnLink: {
        color: colorsPalette.white,
        marginRight: SMALL_MARGIN / 2,
        [theme.breakpoints.up('sm')]: {
          '&:last-child': {
            marginRight: SMALL_MARGIN,
          },
        },
        [theme.breakpoints.up('xl')]: {
          '&:last-child': {
            marginRight: 0,
          },
        },
      },
      joinBtn: {
        marginBottom: SMALL_MARGIN,
        [theme.breakpoints.up('sm')]: {
          marginBottom: 0,
        },
      },
      accBtn: {
        width: 48,
        height: 48,
        cursor: 'pointer',
        marginLeft: SMALL_MARGIN,
        marginRight: SMALL_MARGIN,
        background: colorsPalette.whiteSecondary,
        padding: 12.5,
        transition: '0.5s',
        borderRadius: '999em',
        '&:hover': {
          boxShadow: `0px 3px 6px ${colorsPalette.black}`,
          background: theme.palette.primary.main,
          '& svg': {
            filter:
              'brightness(0) saturate(100%) invert(7%) sepia(28%) saturate(734%) hue-rotate(210deg) brightness(99%) contrast(93%)',
          },
        },

        '&$openSearch': {
          display: 'none',

          [theme.breakpoints.up('md')]: {
            display: 'block',
          },
        },
      },
      overlayContent: {
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      },
      searchWrapper: {
        marginRight: 12,

        [theme.breakpoints.up('md')]: {
          marginLeft: MIDDLE_MARGIN,
          marginRight: MIDDLE_MARGIN,
        },

        '&$openSearch': {
          marginRight: TINY_MARGIN,

          [theme.breakpoints.up('md')]: {
            marginRight: MIDDLE_MARGIN,
          },
        },
      },
      openSearch: {},
      linkWrapper: {
        zIndex: 1,
        position: 'relative',
        marginBottom: 24,
      },
      link: {
        color: colorsPalette.white,
        textTransform: 'uppercase',
        fontWeight: 'bold',
        fontSize: 16,
        lineHeight: 1.2,
      },
      logout: {
        marginTop: SMALL_MARGIN,
        padding: '9px 0',
        background: colorsPalette.violetAnalyticsCard,
        borderRadius: '0 0 10px 10px',
        display: 'flex',
      },
      userInfoWrapper: {
        paddingBottom: 24,
        borderBottom: `1px solid ${alpha(colorsPalette.white, 0.15)}`,
        marginBottom: 24,
      },
      username: {
        color: colorsPalette.white,
        fontSize: 16,
        lineHeight: 1.2,
        fontWeight: 'bold',
        marginBottom: 2,
        zIndex: 2,

        [theme.breakpoints.up('xl')]: {
          marginLeft: SMALL_MARGIN,
        },
      },
      team: {
        fontSize: 14,
        color: colorsPalette.gray,
        lineHeight: 1.5,
      },
      linksWrapper: {
        display: 'flex',
        flexDirection: 'column',
      },
      accordionLinks: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        '& $linkWrapper:last-child': {
          marginBottom: 0,
        },
      },
      overlayMainContent: {
        marginBottom: MIDDLE_MARGIN,
      },
      overlayBottomLink: {
        padding: '24px',
        color: colorsPalette.white,
        textTransform: 'uppercase',
        fontWeight: 'bold',
        fontSize: 16,
        background: colorsPalette.violetAnalyticsCard,
        display: 'block',
        width: '100vw',
        marginLeft: -24,
        marginBottom: -24,
        border: 'none',
        '&:focus': {
          outline: 'none',
        },
        [theme.breakpoints.up('sm')]: {
          marginLeft: -30,
          marginBottom: -30,
          padding: '24px 30px',
        },
      },
      overlayContainer: {
        marginBottom: SMALL_MARGIN,
      },
      menuItemsWithMargin: {
        marginBottom: SMALL_MARGIN,
      },
    });
  });

export default useStyles;
